import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { BlogPostFeedDocument } from '@/generated/contentful'

import { client as contentful } from '@/lib/contentful/client'

import { Button } from '@/components/Button'
import { PostPreview } from '@/components/PostPreview'

import { serializeCacheKey } from '../../../../../lib/contentful/utils'

type Props = {
	className?: string
	hideCategories?: boolean
	hideAuthors?: boolean
	size?: 'medium' | 'large'
	type?: 'Blog Post' | 'Article'
	categorySlug?: string | null
	authorSlug?: string | null
	limit?: number
}

export const DEFAULT_LIMIT = 8

export function BlogPostFeed({
	className,
	hideCategories = false,
	hideAuthors = false,
	categorySlug,
	authorSlug,
	limit = DEFAULT_LIMIT,
	size = 'medium'
}: Props) {
	const { locale, isPreview } = useRouter()
	const client = contentful({ preview: isPreview })
	const [skip, setSkip] = useState(0)
	const { data, isLoading } = useSWR(
		serializeCacheKey('blogPostFeed', {
			categorySlug,
			authorSlug,
			limit,
			skip,
			locale
		}),
		() =>
			client.request(BlogPostFeedDocument, {
				where: {
					categories: { slug: categorySlug },
					author: { slug: authorSlug },
					status: true,
					hideOnHomepage: false
				},
				limit,
				skip,
				locale,
				preview: isPreview
			})
	)
	const [total, setTotal] = useState(data?.blogPostCollection?.total ?? 0)
	const [items, setItems] = useState(data?.blogPostCollection?.items ?? [])

	useEffect(() => {
		setItems((prev) => [
			...prev?.slice(0, skip),
			...(data?.blogPostCollection?.items ?? []),
			...prev?.slice(skip + limit)
		])
	}, [data, limit, skip])

	useEffect(() => {
		if (!data || !data?.blogPostCollection) return

		setTotal(data?.blogPostCollection?.total)
	}, [data])

	return (
		<div className={clsx(className, 'flex flex-col gap-20')} data-testid="blog_post_feed">
			<div
				className={clsx(
					'grid grid-cols-1 gap-x-8 gap-y-24',
					{ medium: 'md:grid-cols-3', large: 'md:grid-cols-2' }[size]
				)}
			>
				{items.map((item) => {
					if (!item || item?.hideOnHomepage) return null

					const image = item?.seoMetadata?.image

					return (
						<PostPreview
							key={item.sys.id}
							className="col-span-1"
							size={size}
							image={
								image && image?.url
									? {
											url: image?.url,
											dimensions: {
												width: image?.width ?? 200,
												height: image?.height ?? 200
											}
									  }
									: null
							}
							imageAlt={image?.title}
							title={item?.title}
							categories={
								hideCategories || !item?.categoriesCollection
									? undefined
									: item?.categoriesCollection?.items?.map((category) => ({
											text: category?.name ?? 'Category',
											link: { href: `/blog/category/${category?.slug ?? ''}` }
									  }))
							}
							author={hideAuthors ? undefined : item?.author?.fullName}
							link={{ href: `/blog/${item?.slug}` }}
						/>
					)
				})}
			</div>
			{items?.length < total && (
				<div className="flex justify-center">
					<Button
						onClick={() => {
							if (items?.length < total) {
								setSkip((prev) => prev + limit)
							}
						}}
						disabled={isLoading}
						size="large"
						color="secondary"
						variant="filled"
						isLoading={isLoading}
					>
						View more
					</Button>
				</div>
			)}
		</div>
	)
}
